import { z } from 'zod';
import { ApiClient } from '@/common/api/ApiClient';
import {
  variantSchema,
  productSchema,
  type Variant,
  type Product,
  type CreateProductRequest,
} from './schemas';

// ─── Mock variants ────────────────────────────────────────────────────────────

const MOCK_VARIANTS: Variant[] = [
  {
    id: 1,
    name: 'Solar Production Forecast',
    productType: {
      name: 'Solar Production Forecast',
      systemTypes: [{ id: 1, name: 'Type 1' }],
      validFrom: '2024-01-01',
      validTo: null,
      dependencies: [],
      description:
        'Real-time and predictive monitoring of your solar panel output.\n\n**Includes:**\n- 15-minute resolution forecasts\n- Historical production analysis\n- Weather-based predictions',
    },
    priceSet: [
      {
        validFrom: '2024-01-01',
        validTo: null,
        currency: { name: 'CZK', rateToEur: '25.0' },
        vatRate: '21.0',
        priceMonthly: 490,
        priceAnnual: 4900,
      },
    ],
  },
  {
    id: 2,
    name: 'Solar Panel Diagnostics',
    productType: {
      name: 'Solar Panel Diagnostics',
      systemTypes: [{ id: 1, name: 'Type 1' }],
      validFrom: '2024-01-01',
      validTo: null,
      dependencies: [],
      description:
        'Real-time and predictive monitoring of your solar panel output.\n\n**Includes:**\n- 15-minute resolution forecasts\n- Historical production analysis\n- Weather-based predictions',
    },
    priceSet: [
      {
        validFrom: '2024-01-01',
        validTo: null,
        currency: { name: 'CZK', rateToEur: '25.0' },
        vatRate: '21.0',
        priceMonthly: 490,
        priceAnnual: 4900,
      },
    ],
  },
  {
    id: 3,
    name: 'SPOT Price Optimization',
    productType: {
      name: 'SPOT Price Optimization',
      systemTypes: [{ id: 1, name: 'Type 1' }],
      validFrom: '2024-01-01',
      validTo: null,
      dependencies: [1, 2],
    },
    priceSet: [
      {
        validFrom: '2024-01-01',
        validTo: null,
        currency: { name: 'CZK', rateToEur: '25.0' },
        vatRate: '21.0',
        priceMonthly: 990,
        priceAnnual: 9900,
      },
    ],
  },
  {
    id: 4,
    name: 'Flexibility Prediction',
    productType: {
      name: 'Flexibility Prediction',
      systemTypes: [{ id: 1, name: 'Type 1' }],
      validFrom: '2024-01-01',
      validTo: null,
      dependencies: [],
      description:
        'Real-time and predictive monitoring of your solar panel output.\n\n**Includes:**\n- 15-minute resolution forecasts\n- Historical production analysis\n- Weather-based predictions',
    },
    priceSet: [
      {
        validFrom: '2024-01-01',
        validTo: null,
        currency: { name: 'CZK', rateToEur: '25.0' },
        vatRate: '21.0',
        priceMonthly: 1490,
        priceAnnual: 14900,
      },
    ],
  },
  {
    id: 5,
    name: 'Flexibility Valuation',
    productType: {
      name: 'Flexibility Valuation',
      systemTypes: [{ id: 1, name: 'Type 1' }],
      validFrom: '2024-01-01',
      validTo: null,
      dependencies: [4],
    },
    priceSet: [
      {
        validFrom: '2024-01-01',
        validTo: null,
        currency: { name: 'CZK', rateToEur: '25.0' },
        vatRate: '21.0',
        priceMonthly: 1990,
        priceAnnual: 19900,
      },
    ],
  },
  {
    id: 6,
    name: 'Aggregator Control',
    productType: {
      name: 'Aggregator Control',
      systemTypes: [{ id: 1, name: 'Type 1' }],
      validFrom: '2024-01-01',
      validTo: null,
      dependencies: [4, 5],
    },
    priceSet: [
      {
        validFrom: '2024-01-01',
        validTo: null,
        currency: { name: 'CZK', rateToEur: '25.0' },
        vatRate: '21.0',
        priceMonthly: 2990,
        priceAnnual: 29900,
      },
    ],
  },
];

// ─── Mock products per supply point ──────────────────────────────────────────

const mockProducts: Product[] = [
  {
    id: 1,
    variant: 1,
    system: 1,
    status: 'active' as const,
    billingContact: 1,
    billingFrequency: 'annual',
  },
];

let nextProductId = 2;

// ─── Service ──────────────────────────────────────────────────────────────────

export const ProductService = {
  getVariants: async () => {
    return ApiClient.get('/product/variant/', z.array(variantSchema), undefined, {
      mock: () => MOCK_VARIANTS.map((v) => ({ ...v })),
    });
  },

  getProducts: async (supplyPointId: number) => {
    const all = await ApiClient.get('/product/product/', z.array(productSchema), undefined, {
      mock: () => mockProducts.filter((p) => p.system === supplyPointId).map((p) => ({ ...p })),
    });
    return all.filter((p) => p.system === supplyPointId);
  },

  deleteProduct: async (id: number) => {
    return ApiClient.delete(`/product/product/${id}`, undefined, undefined, {
      mock: () => {
        const index = mockProducts.findIndex((p) => p.id === id);
        if (index !== -1) mockProducts.splice(index, 1);
      },
    });
  },

  createProduct: async (data: CreateProductRequest) => {
    return ApiClient.post('/product/product/', data, productSchema, {
      mock: () => {
        const newProduct: Product = {
          id: nextProductId++,
          variant: data.variant,
          system: data.system,
          status: 'pending',
          billingContact: data.billingContact,
          billingFrequency: data.billingFrequency,
        };
        mockProducts.push(newProduct);
        return { ...newProduct };
      },
    });
  },
};
